import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Dropdown, Tag, Typography, Button, message, Table as AntTable } from 'antd';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import { useAsync } from 'react-async-hook';
import { Finance, ChevronDown, Purchase } from '@carbon/icons-react';
import Card from 'components/Card/Card';
import { formatAddress } from 'components/FormattedAddress/FormattedAddress';
import { EMDASH } from 'constants';
import { providerService } from 'services';
import { PROVIDER_APPLICATION_PROGRAM_TYPES } from 'containers/Provider/SelectProgram/SelectProgram';

const { Text } = Typography;

const getProgramTypeName = (application) => {
  return (
    Object.values(PROVIDER_APPLICATION_PROGRAM_TYPES).find((pt) => pt.id === application?.programType?.id)?.name ||
    application?.programType?.name ||
    EMDASH
  );
};

const renderTaxProfile = (profile) => {
  if (!profile) return EMDASH;
  return (
    <div>
      <div className="font-medium leading-tight">{profile.profileName || 'Unnamed Profile'}</div>
      {profile.taxClassification?.title && (
        <div className="text-xs text-gray-400 leading-tight mt-0.5">{profile.taxClassification.title}</div>
      )}
    </div>
  );
};

const renderBankingProfile = (profile) => {
  if (!profile) return EMDASH;
  return (
    <div>
      <div className="font-medium leading-tight">{profile.profileName || profile.name || 'Unnamed Profile'}</div>
      <div className="text-xs text-gray-400 leading-tight mt-0.5">
        {profile.directDeposit ? 'Direct Deposit' : 'Paper Check'}
        {!profile.directDeposit && profile.paymentName && ` · ${profile.paymentName}`}
        {profile.bankName && ` · ${profile.bankName}`}
        {profile.accountNumber && ` ····${String(profile.accountNumber).slice(-4)}`}
      </div>
    </div>
  );
};

export default function Locations({ careFacilities, orgId, onRefetch, isEditable = true }) {
  const history = useHistory();
  const [reassigning, setReassigning] = useState(false);
  const [orgProfiles, setOrgProfiles] = useState([]);
  const [orgTaxProfiles, setOrgTaxProfiles] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  useAsync(async () => {
    if (!orgId) return;
    try {
      const profiles = await providerService.getBankingProfiles(orgId);
      setOrgProfiles(profiles || []);
    } catch (error) {
      console.error('Failed to fetch org banking profiles', error);
    }
  }, [orgId]);

  useAsync(async () => {
    if (!orgId) return;
    try {
      const profiles = await providerService.getTaxProfiles(orgId);
      setOrgTaxProfiles(profiles || []);
    } catch (error) {
      console.error('Failed to fetch org tax profiles', error);
    }
  }, [orgId]);

  const handleReassignBanking = async (facilityId, profileId) => {
    try {
      setReassigning(true);
      await providerService.activateBankingProfile(facilityId, profileId);
      message.success('Banking profile updated');
      onRefetch?.();
    } catch (error) {
      message.error('Failed to reassign banking profile');
    } finally {
      setReassigning(false);
    }
  };

  const handleReassignTax = async (facilityId, profileId) => {
    try {
      setReassigning(true);
      await providerService.activateTaxProfileOnFacility(facilityId, profileId);
      message.success('Tax profile updated');
      onRefetch?.();
    } catch (error) {
      message.error('Failed to reassign tax profile');
    } finally {
      setReassigning(false);
    }
  };

  const handleBulkReassignBanking = async (profileId) => {
    try {
      setReassigning(true);
      await Promise.all(
        selectedRowKeys.map((facilityId) => providerService.activateBankingProfile(facilityId, profileId)),
      );
      message.success('Banking profile updated');
      onRefetch?.();
      setSelectedRowKeys([]);
    } catch (error) {
      message.error('Failed to reassign banking profile');
    } finally {
      setReassigning(false);
    }
  };

  const handleBulkReassignTax = async (profileId) => {
    try {
      setReassigning(true);
      await Promise.all(
        selectedRowKeys.map((facilityId) => providerService.activateTaxProfileOnFacility(facilityId, profileId)),
      );
      message.success('Tax profile updated');
      onRefetch?.();
      setSelectedRowKeys([]);
    } catch (error) {
      message.error('Failed to reassign tax profile');
    } finally {
      setReassigning(false);
    }
  };

  const toggleExpand = (facilityId) => {
    setExpandedRowKeys((prev) =>
      prev.includes(facilityId) ? prev.filter((k) => k !== facilityId) : [...prev, facilityId],
    );
  };

  const facilityColumns = [
    {
      title: 'Location',
      key: 'location',
      width: '50%',
      render: (_, facility) => {
        const address = formatAddress(facility.address);

        return (
          <div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-semibold">{facility.displayName || EMDASH}</span>

              <span className="text-gray-400 text-sm">ID: {facility.id}</span>

              {facility.applications?.length > 0 && (
                <span className="text-gray-400 text-sm">
                  &middot; {facility.applications.length} application{facility.applications.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            {address && <div className="text-sm text-gray-400 mt-0.5">{address}</div>}
          </div>
        );
      },
    },
    {
      title: 'Banking Profile',
      key: 'bankingProfile',
      width: '25%',
      render: (_, facility) => {
        const profile = facility.banking ?? facility.applications?.[0]?.careFacility?.banking;
        return renderBankingProfile(profile);
      },
    },
    {
      title: 'Tax Profile',
      key: 'taxProfile',
      width: '25%',
      render: (_, facility) => renderTaxProfile(facility.taxProfile ?? facility.applications?.[0]?.taxProfile),
    },
    {
      key: 'actions',
      render: (_, facility) => {
        if (!isEditable || (orgProfiles.length === 0 && orgTaxProfiles.length === 0)) return null;

        const currentBankingId = facility.banking?.id ?? facility.applications?.[0]?.careFacility?.banking?.id;
        const currentTaxId = facility.taxProfile?.id ?? facility.applications?.[0]?.taxProfile?.id;

        const bankingMenuItems = orgProfiles.map((profile) => ({
          key: `banking-${profile.id}`,
          disabled: profile.id === currentBankingId,
          label: (
            <div className="flex items-center justify-between gap-6 py-0.5">
              <div>
                <div className="font-medium leading-tight">{profile.profileName || 'Unnamed'}</div>
                <div className="text-xs text-gray-400 leading-tight mt-0.5">
                  {profile.directDeposit ? 'Direct Deposit' : 'Paper Check'}
                  {!profile.directDeposit && profile.paymentName && ` · ${profile.paymentName}`}
                  {profile.bankName && ` · ${profile.bankName}`}
                  {profile.accountNumber && ` ····${String(profile.accountNumber).slice(-4)}`}
                </div>
              </div>
              {profile.id === currentBankingId && (
                <Tag color="blue" className="shrink-0 mr-0">
                  Current
                </Tag>
              )}
            </div>
          ),
          onClick: () => handleReassignBanking(facility.id, profile.id),
        }));

        const taxMenuItems = orgTaxProfiles.map((profile) => ({
          key: `tax-${profile.id}`,
          disabled: profile.id === currentTaxId,
          label: (
            <div className="flex items-center justify-between gap-6 py-0.5">
              <div>
                <div className="font-medium leading-tight">{profile.profileName || 'Unnamed'}</div>
                {profile.taxClassification?.title && (
                  <div className="text-xs text-gray-400 leading-tight mt-0.5">{profile.taxClassification.title}</div>
                )}
              </div>
              {profile.id === currentTaxId && (
                <Tag color="blue" className="shrink-0 mr-0">
                  Current
                </Tag>
              )}
            </div>
          ),
          onClick: () => handleReassignTax(facility.id, profile.id),
        }));

        const menuItems = [
          ...(orgProfiles.length > 0
            ? [
                {
                  key: 'reassign-banking',
                  icon: <Purchase size={16} className="mt-[2px]" />,
                  label: 'Reassign Banking Profile',
                  children: bankingMenuItems,
                },
              ]
            : []),
          ...(orgTaxProfiles.length > 0
            ? [
                {
                  key: 'reassign-tax',
                  icon: <Finance size={16} className="mt-[2px]" />,
                  label: 'Reassign Tax Profile',
                  children: taxMenuItems,
                },
              ]
            : []),
        ];

        return (
          <div onClick={(e) => e.stopPropagation()}>
            <Dropdown menu={{ items: menuItems }} trigger={['click']} disabled={reassigning}>
              <Button size="small" className="text-gray-600">
                Actions <ChevronDown size={14} className="ml-0.5 inline-block align-middle" />
              </Button>
            </Dropdown>
          </div>
        );
      },
    },
  ];

  const applicationColumns = [
    {
      title: 'Application',
      key: 'id',
      dataIndex: 'id',
      render: (val) => (
        <Link to={`/providers/applications/${val}`} className="underline">
          {val}
        </Link>
      ),
    },
    {
      title: 'Program',
      key: 'program',
      render: (_, application) => <Tag>{getProgramTypeName(application)}</Tag>,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, application) => {
        return <Tag color="default">{application?.status?.status?.description}</Tag>;
      },
    },
  ];

  const toolbarBankingItems = orgProfiles.map((profile) => ({
    key: `toolbar-banking-${profile.id}`,
    label: (
      <div>
        <div className="font-medium leading-tight">{profile.profileName || 'Unnamed'}</div>
        <div className="text-xs text-gray-400 leading-tight mt-0.5">
          {profile.directDeposit ? 'Direct Deposit' : 'Paper Check'}
          {!profile.directDeposit && profile.paymentName && ` · ${profile.paymentName}`}
          {profile.bankName && ` · ${profile.bankName}`}
        </div>
      </div>
    ),
    onClick: () => handleBulkReassignBanking(profile.id),
  }));

  const toolbarTaxItems = orgTaxProfiles.map((profile) => ({
    key: `toolbar-tax-${profile.id}`,
    label: (
      <div>
        <div className="font-medium leading-tight">{profile.profileName || 'Unnamed'}</div>
        {profile.taxClassification?.title && (
          <div className="text-xs text-gray-400 leading-tight mt-0.5">{profile.taxClassification.title}</div>
        )}
      </div>
    ),
    onClick: () => handleBulkReassignTax(profile.id),
  }));

  return (
    <>
      {isEditable && selectedRowKeys.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex flex-wrap items-center gap-3 bg-gray-900 text-white px-5 py-3 rounded-lg shadow-xl w-[calc(100%-2rem)] max-w-max md:w-auto">
          <div className="flex items-center justify-between w-full md:w-auto md:justify-start gap-2">
            <span className="text-sm font-medium whitespace-nowrap">
              {selectedRowKeys.length} location{selectedRowKeys.length !== 1 ? 's' : ''} selected
            </span>
            <button
              onClick={() => setSelectedRowKeys([])}
              className="text-gray-400 hover:text-white text-lg leading-none"
            >
              ×
            </button>
          </div>

          <div className="flex flex-wrap gap-2 w-full md:w-auto">
            {toolbarBankingItems.length > 0 && (
              <Dropdown menu={{ items: toolbarBankingItems }} trigger={['click']} disabled={reassigning}>
                <Button
                  size="small"
                  className="!bg-gray-700 !border-gray-600 !text-white hover:!bg-gray-600 flex items-center gap-1.5 flex-1 md:flex-none justify-center"
                >
                  <Purchase size={14} /> Assign Banking Profile
                </Button>
              </Dropdown>
            )}
            {toolbarTaxItems.length > 0 && (
              <Dropdown menu={{ items: toolbarTaxItems }} trigger={['click']} disabled={reassigning}>
                <Button
                  size="small"
                  className="!bg-gray-700 !border-gray-600 !text-white hover:!bg-gray-600 flex items-center gap-1.5 flex-1 md:flex-none justify-center"
                >
                  <Finance size={14} /> Assign Tax Profile
                </Button>
              </Dropdown>
            )}
          </div>
        </div>
      )}
      <Card
        title={`Locations (${careFacilities?.length ?? 0})`}
        noBodyPadding
        styles={{
          body: {
            overflow: 'hidden',
          },
        }}
      >
        <AntTable
          dataSource={careFacilities ?? []}
          columns={facilityColumns}
          rowKey="id"
          pagination={false}
          rowSelection={
            isEditable
              ? {
                  selectedRowKeys,
                  onChange: (keys) => setSelectedRowKeys(keys),
                }
              : undefined
          }
          onRow={(facility) => ({
            onClick: () => toggleExpand(facility.id),
            style: { cursor: 'pointer' },
          })}
          expandable={{
            expandedRowKeys,
            onExpandedRowsChange: setExpandedRowKeys,
            rowExpandable: (facility) => !!facility.applications?.length,
            expandedRowRender: (facility) =>
              facility.applications?.length ? (
                <AntTable
                  dataSource={facility.applications}
                  columns={applicationColumns}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  onRow={(application) => ({
                    onClick: () => history.push(`/providers/applications/${application.id}`),
                    style: { cursor: 'pointer' },
                  })}
                />
              ) : (
                <Text type="secondary">No applications for this location</Text>
              ),
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>
    </>
  );
}
